import { useEffect } from 'react';

import { ListenerPayload, OptimizelyDecideOption, enums } from '@optimizely/react-sdk';

import { optimizelyClient } from '../app/utils/featureflag/FeatureFlagsProvider';
import { FullStoryFeatureFlags } from '../app/utils/featureflag/featureFlags';
import { SetUserVariables } from './usePortalFullStoryUserVars';

type Decision = {
  decisionInfo: {
    flagKey: string;
    variationKey: string;
  };
  type: string;
} & ListenerPayload;

const getFeatureFlags = () => {
  if (!optimizelyClient.isReady()) return null;

  return Object.fromEntries(
    Object.entries(
      // We don't want to send decision events to Optimizely for this call
      optimizelyClient.decideAll([OptimizelyDecideOption.DISABLE_DECISION_EVENT]),
    )
      .filter(([key]) => FullStoryFeatureFlags[key])
      .map(([key, value]) => [key, value.variationKey || 'off']),
  );
};

/**
 * Forwards Optimizely's flag decisions to FullStory.
 */
export const usePortalFullStoryFeatureFlags = ({
  setUserVariables,
}: {
  setUserVariables: SetUserVariables;
}) => {
  const isOptimizelyReady = optimizelyClient.isReady();

  // send initial feature flag data
  useEffect(() => {
    if (!isOptimizelyReady) return;
    setUserVariables({ featureFlags: getFeatureFlags() });
  }, [isOptimizelyReady, setUserVariables]);

  // Updates feature flags on Optimizely's flag decisions
  useEffect(() => {
    optimizelyClient.notificationCenter.addNotificationListener<Decision>(
      enums.NOTIFICATION_TYPES.DECISION,
      (decision) => {
        if (decision.type !== enums.DECISION_NOTIFICATION_TYPES.FLAG) return;

        const { flagKey, variationKey } = decision.decisionInfo || {};
        if (!flagKey || !variationKey) return;

        if (!FullStoryFeatureFlags[flagKey]) return;

        setUserVariables((current) => ({
          ...current,
          featureFlags: {
            ...(current.featureFlags || {}),
            [flagKey]: variationKey,
          },
        }));
      },
    );
  }, [setUserVariables]);
};
